import { useEffect, useState, useCallback } from 'react';
import { usePlatformStore } from '@/lib/store';
import { fetchTenantPricing, updateTenantPricing } from '@/lib/api';
import { B2C_PLAN_ORDER, B2C_PLAN_DISPLAY } from '@/lib/constants';
import { Search, Save, Loader2, DollarSign } from 'lucide-react';
import { cn } from '@/utils/helpers';
import type { AppTenantPricingPlan } from '@/types';

interface Props {
  defaultTenantId: string;
}

interface PlanEditState {
  monthly_price: number;
  annual_price: number;
  is_active: boolean;
}

export function PricingTab({ defaultTenantId }: Props) {
  const { tenants, loadTenants } = usePlatformStore();
  const [selectedTenantId, setSelectedTenantId] = useState(defaultTenantId);
  const [plans, setPlans] = useState<AppTenantPricingPlan[]>([]);
  const [edits, setEdits] = useState<Record<string, PlanEditState>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => { loadTenants(); }, [loadTenants]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchTenantPricing(selectedTenantId);
      setPlans(data);

      const e: Record<string, PlanEditState> = {};
      for (const p of data) {
        e[p.id] = {
          monthly_price: p.monthly_price,
          annual_price: p.annual_price,
          is_active: p.is_active,
        };
      }
      setEdits(e);
      setDirty(false);
    } catch (err) {
      console.error('Failed to load pricing:', err);
    }
    setLoading(false);
  }, [selectedTenantId]);

  useEffect(() => { load(); }, [load]);

  const updateField = (planId: string, field: keyof PlanEditState, value: number | boolean) => {
    setEdits(prev => ({
      ...prev,
      [planId]: { ...prev[planId], [field]: value },
    }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const planUpdates = plans.map(p => {
        const edit = edits[p.id];
        return {
          plan_key: p.plan_key,
          name: p.name,
          description: p.description,
          monthly_price: edit?.monthly_price ?? p.monthly_price,
          annual_price: edit?.annual_price ?? p.annual_price,
          is_active: edit?.is_active ?? p.is_active,
          sort_order: p.sort_order,
        };
      });
      await updateTenantPricing(selectedTenantId, planUpdates as any);
      setDirty(false);
      await load();
    } catch (err) {
      console.error('Failed to save pricing:', err);
    }
    setSaving(false);
  };

  const sortedPlans = [...plans].sort((a, b) => {
    const ai = B2C_PLAN_ORDER.indexOf(a.plan_key as typeof B2C_PLAN_ORDER[number]);
    const bi = B2C_PLAN_ORDER.indexOf(b.plan_key as typeof B2C_PLAN_ORDER[number]);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  // Quick tenant list for sidebar
  const filteredTenants = tenants.filter(t =>
    !search || t.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4">
      <p className="text-sm text-surface-500">
        Control B2C plan pricing per tenant. Changes are reflected in the B2C app's PricingPage in real time.
      </p>

      <div className="flex gap-4">
        {/* Tenant selector sidebar */}
        <div className="w-56 shrink-0 space-y-2">
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-surface-500" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search..."
              className="input-field pl-8 py-1.5 text-xs w-full" />
          </div>
          <div className="space-y-1 max-h-[400px] overflow-y-auto">
            {filteredTenants.map(t => (
              <button key={t.id}
                onClick={() => setSelectedTenantId(t.id)}
                className={cn('w-full text-left px-3 py-2 rounded-lg text-xs transition-colors',
                  selectedTenantId === t.id ? 'badge-brand font-medium' : 'text-surface-600 hover:bg-surface-50')}>
                {t.name}
              </button>
            ))}
          </div>
        </div>

        {/* Pricing grid */}
        <div className="flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="animate-spin text-brand-600 dark:text-brand-400" size={24} />
            </div>
          ) : sortedPlans.length === 0 ? (
            <div className="text-center py-12 text-sm text-surface-500">
              No pricing plans found for this tenant.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-surface-900">
                  {tenants.find(t => t.id === selectedTenantId)?.name || 'Tenant'} — Plan Pricing
                </h3>
                {dirty && (
                  <button onClick={handleSave} disabled={saving}
                    className="btn-primary flex items-center gap-2 px-4 py-2 text-sm">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    Save Changes
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {sortedPlans.map(plan => {
                  const edit = edits[plan.id];
                  if (!edit) return null;
                  const display = B2C_PLAN_DISPLAY[plan.plan_key];

                  return (
                    <div key={plan.id} className="glass-panel overflow-hidden">
                      <div className="flex items-center gap-3 px-5 py-3 border-b border-surface-200">
                        <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: display?.color || '#999' }} />
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-surface-900">{display?.name || plan.plan_key}</div>
                          <div className="text-[10px] text-surface-500">{plan.description || plan.plan_key}</div>
                        </div>
                        <button
                          onClick={() => updateField(plan.id, 'is_active', !edit.is_active)}
                          className={cn('relative w-9 h-5 rounded-full transition-colors',
                            edit.is_active ? 'bg-brand-500' : 'bg-surface-200')}>
                          <span className={cn('block w-4 h-4 rounded-full bg-white shadow transform transition-transform mt-0.5',
                            edit.is_active ? 'translate-x-4 ml-0.5' : 'translate-x-0.5')} />
                        </button>
                      </div>

                      <div className="p-5 space-y-3">
                        {/* Monthly Price */}
                        <div>
                          <label className="block text-[10px] font-medium text-surface-500 mb-1">Monthly Price (cents)</label>
                          <div className="relative">
                            <DollarSign size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-surface-400" />
                            <input type="number"
                              value={edit.monthly_price}
                              onChange={e => updateField(plan.id, 'monthly_price', parseInt(e.target.value, 10) || 0)}
                              className="input-field pl-7 py-1.5 w-full text-sm" />
                          </div>
                          <div className="text-[10px] text-surface-400 mt-0.5">
                            = ${(edit.monthly_price / 100).toFixed(2)}/mo
                          </div>
                        </div>

                        {/* Annual Price */}
                        <div>
                          <label className="block text-[10px] font-medium text-surface-500 mb-1">Annual Price (cents/mo equivalent)</label>
                          <div className="relative">
                            <DollarSign size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-surface-400" />
                            <input type="number"
                              value={edit.annual_price}
                              onChange={e => updateField(plan.id, 'annual_price', parseInt(e.target.value, 10) || 0)}
                              className="input-field pl-7 py-1.5 w-full text-sm" />
                          </div>
                          <div className="text-[10px] text-surface-400 mt-0.5">
                            = ${(edit.annual_price / 100).toFixed(2)}/mo (${((edit.annual_price * 12) / 100).toFixed(2)}/yr)
                          </div>
                        </div>

                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
